import { useMemo, useState } from "react"
import { format, isValid } from "date-fns"

import { Button, Select } from "@productlane/ui"

interface CreatedAtStepProps {
	data: string[][]
	hasHeaders: boolean
	sortedData: string[][]
	createdAtIndex: number
	setCreatedAtIndex: (index: number) => void
	back: () => void
	next: () => void
}

interface Header {
	label: string
	value: string
}

export function CreatedAtStep({
	data,
	hasHeaders,
	sortedData,
	createdAtIndex,
	setCreatedAtIndex,
	back,
	next,
}: CreatedAtStepProps) {
	const [isValidDate, setIsValidDate] = useState<boolean>(true)

	const headers = useMemo<Array<Header>>(() => {
		if (!data[0]) return []

		if (hasHeaders) {
			return data[0].map((it, i) => ({
				label: it,
				value: i.toString(),
			}))
		}

		return data[0].map((_, i) => ({
			label: `${i + 1}. Column`,
			value: i.toString(),
		}))
	}, [hasHeaders, data])

	const handleChange = (index: string) => {
		setCreatedAtIndex(Number(index))
		setIsValidDate(validDate(Number(index)))
	}

	const validDate = (index: number): boolean => {
		const dates = sortedData[index]
		if (!dates?.length) return true

		const lastDate = dates[dates.length - 1]
		return Boolean(lastDate && isValid(new Date(lastDate)))
	}

	return (
		<div>
			<Select
				value={createdAtIndex.toString()}
				label="Select the column containing createdAt"
				name="created-at"
				options={[{ label: "There are no dates", value: "-1" }, ...headers]}
				onChange={handleChange}
				showErrorMessage={!isValidDate}
				errorMessage={!isValidDate ? "Invalid dates found" : undefined}
			/>
			{createdAtIndex !== -1 && (
				<>
					<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
					<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 text-sm dark:bg-gray-900">
						{sortedData[createdAtIndex]
							?.slice(hasHeaders ? 1 : 0, 4)
							.map((date, i) => (
								<div
									key={`date-${i}`}
									className="flex items-center gap-2 truncate"
								>
									<span className="text-primary">
										{format(new Date(date), "EEE, MMM d 'at' h:mm a")}
									</span>
									<span className="text-secondary">{date}</span>
								</div>
							))}
						<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
					</div>
				</>
			)}
			<div className="mt-2 flex justify-between">
				<Button variant="link" onClick={back}>
					Back
				</Button>
				<Button onClick={next}>Next</Button>
			</div>
		</div>
	)
}
